import "./JSONformatter.css";
import ToolHeader from "../../Components/ToolHeader";
import { Row, Col } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import FormatterPanel from "../../HomePage/FormatterPanel";
import { useEffect, useState } from "react";
import { ReactComponent as CheckGreen } from "../../Icons/checkGreen.svg";
import { ReactComponent as CheckRed } from "../../Icons/CheckRed.svg";
import { ReactComponent as CheckBlue } from "../../Icons/CheckBlue.svg";

function JSONDiffViewer() {
    const location = useLocation();
    const {
        baseJson = "",
        baseText = "",
        baseXml = "",
        baseCsv = "",
        validInputs = [],
        comparisons = {},
        diffType: locationDiffType = "JSON"
    } = location.state || {};

    const diffType = (locationDiffType || (baseText ? "TEXT" : baseXml ? "XML" : baseCsv ? "CSV" : "JSON")).toUpperCase();
    const typeLabels = {
        JSON: "JSON",
        TEXT: "Text",
        XML: "XML",
        CSV: "CSV"
    };
    const typeLabel = typeLabels[diffType] || "Input";
    const baseContent = diffType === "TEXT" ? baseText : diffType === "XML" ? baseXml : diffType === "CSV" ? baseCsv : baseJson;
    const results = Object.entries(comparisons || {});
    
    const [activeIndex, setActiveIndex] = useState(0);
    const [inactiveItems, setInactiveItems] = useState([]);

    const activeItemDiff = results[activeIndex]?.[1] || {};
    const activeJsonContent = validInputs[activeIndex] || "";

    useEffect(() => {
        const remaining = validInputs
            .map((content, index) => ({ content, originalIndex: index }))
            .filter(item => item.originalIndex !== activeIndex);
            
        setInactiveItems(remaining);
    }, [activeIndex, validInputs]);

    const normalizePath = (path) => {
        const normalized = String(path || "")
            .replace(/^\$/, "")
            .replace(/\./g, "/")
            .replace(/\[(\d+)\]/g, "/$1")
            .replace(/\/+/g, "/");

        if (!normalized) return "";
        return normalized.startsWith("/") ? normalized : `/${normalized}`;
    };

    const pathMatches = (diffPath, currentPath) => {
        const diff = normalizePath(diffPath);
        const current = normalizePath(currentPath);

        if (!diff || !current) return false;
        return current === diff || current.startsWith(`${diff}/`) || diff.startsWith(`${current}/`);
    };

    const getPathClass = (path, item, side = "compare") => {
        if (side === "compare" && item?.added?.some(p => pathMatches(p, path))) return "diff-added";
        if (side === "base" && item?.removed?.some(p => pathMatches(p, path))) return "diff-removed";
        if (item?.modified?.some(p => pathMatches(p, path))) return "diff-changed";
        return "";
    };

    const escapeHtml = (value) =>
        String(value || "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");

    const renderDiffLine = (content, path = "", applyColor = true, diffData = null, side = "compare") => {
        const cls = applyColor && diffData && path ? getPathClass(path, diffData, side) : "";
        return `<div class="diff-line ${cls}"><span>${escapeHtml(content)}</span></div>`;
    };

    const renderColorizedJSON = (jsonStr, applyColor = true, diffData = null, side = "compare") => {
        try {
            if (!jsonStr) return "";
            const parsed = JSON.parse(jsonStr);

            const renderNode = (value, path = "", indent = "", trailingComma = false, key = null) => {
                const keyPrefix = key === null ? "" : `"${key}": `;
                const comma = trailingComma ? "," : "";

                if (Array.isArray(value)) {
                    let html = renderDiffLine(`${indent}${keyPrefix}[`, path, applyColor, diffData, side);

                    value.forEach((item, index) => {
                        const childPath = `${path}/${index}`;
                        html += renderNode(item, childPath, `${indent}  `, index < value.length - 1);
                    });

                    html += renderDiffLine(`${indent}]${comma}`);
                    return html;
                }

                if (value && typeof value === "object") {
                    const entries = Object.entries(value);
                    let html = renderDiffLine(`${indent}${keyPrefix}{`, path, applyColor, diffData, side);

                    entries.forEach(([childKey, childValue], index) => {
                        const childPath = `${path}/${childKey}`;
                        html += renderNode(
                            childValue,
                            childPath,
                            `${indent}  `,
                            index < entries.length - 1,
                            childKey
                        );
                    });

                    html += renderDiffLine(`${indent}}${comma}`);
                    return html;
                }

                return renderDiffLine(
                    `${indent}${keyPrefix}${JSON.stringify(value)}${comma}`,
                    path,
                    applyColor,
                    diffData,
                    side
                );
            };

            return renderNode(parsed, "", "");
        } catch (e) {
            return escapeHtml(jsonStr);
        }
    };

    const renderColorizedText = (text, applyColor = true, diffData = null, side = "compare") => {
        if (!text) return "";
        return text
            .split(/\r?\n/)
            .map((line, index) => {
                const cls = applyColor ? getPathClass(`/${index}`, diffData, side) : "";
                return `<div class="diff-line ${cls}"><span>${escapeHtml(line)}</span></div>`;
            })
            .join("");
    };

    const renderColorizedCSV = (csvStr, applyColor = true, diffData = null, side = "compare") => {
        if (!csvStr) return "";
        return csvStr
            .split(/\r?\n/)
            .map((line, index) => {
                const cls = applyColor ? getPathClass(`/${index}`, diffData, side) : "";
                return `<div class="diff-line ${cls}"><span>${escapeHtml(line)}</span></div>`;
            })
            .join("");
    };

    const renderColorizedXML = (xmlStr, applyColor = true, diffData = null, side = "compare") => {
        if (!xmlStr) return "";
        const pretty = xmlStr
            .replace(/>\s*</g, ">\n<")
            .replace(/\r/g, "");
        const lines = pretty.split(/\n/);
        let pathStack = [];
        let html = "";

        lines.forEach((line) => {
            const trimmed = line.trim();
            const closingTag = trimmed.match(/^<\/(\w[\w:-]*)>/);
            const openingTag = trimmed.match(/^<(\w[\w:-]*)(\s|>|\/)/);
            const selfClosing = trimmed.match(/^<(\w[\w:-]*)([^>]*)\/>/);

            if (closingTag) {
                pathStack.pop();
            }

            if (openingTag) {
                pathStack[pathStack.length - 1] = openingTag[1];
            }

            const currentPath = "/" + pathStack.filter(Boolean).join("/");
            const cls = applyColor ? getPathClass(currentPath, diffData, side) : "";
            html += `<div class="diff-line ${cls}"><span>${escapeHtml(line)}</span></div>`;

            if (openingTag && !selfClosing && !trimmed.startsWith("</") && !trimmed.startsWith("<?") && !trimmed.startsWith("<!")) {
                pathStack.push("");
            }
        });

        return html;
    };

    const renderColorizedContent = (content, applyColor = true, diffData = null, side = "compare") => {
        switch (diffType) {
            case "TEXT":
                return renderColorizedText(content, applyColor, diffData, side);
            case "CSV":
                return renderColorizedCSV(content, applyColor, diffData, side);
            case "XML":
                return renderColorizedXML(content, applyColor, diffData, side);
            case "JSON":
            default:
                return renderColorizedJSON(content, applyColor, diffData, side);
        }
    };

    return (
        <Col>
            {/* HEADER */}
            <ToolHeader
                title={`${typeLabel} Comparison Results`}
                description={`Compare a base ${typeLabel.toLowerCase()} with multiple ${typeLabel.toLowerCase()} inputs`}
                showFavourite={false}
            />

            {/* SUMMARY CARDS */}
            <Row className="ActionButtons diff-action-buttons">
                {results.map(([key, comp], index) => (
                    <Col
                        key={key}
                        onClick={() => setActiveIndex(index)}
                        className={`ActionButton diff-action-button ${activeIndex === index ? "active" : ""}`}
                        style={{ 
                            cursor: "pointer",
                            backgroundColor: activeIndex === index ? "#4F3CFA1A" : "" 
                        }}
                    >
                        <span style={{ color:  "var(--text-color)" }}>{typeLabel} {index + 1}</span>
                        <span style={{ color: "#21C55E", padding: "4px 8px" }}>{comp.added?.length || 0} Added</span>
                        <span style={{ color: "#EF4444", padding: "4px 8px" }}>{comp.removed?.length || 0} Removed</span>
                        <span style={{ color: "#3B82F6", padding: "4px 8px" }}>{comp.modified?.length || 0} Modified</span>
                    </Col>
                ))}
            </Row>

            {/* LEGEND + SELECTOR */}
            <Row className="Introduction-Header align-items-center">
                <Col md={6} className="diff-legend">
                    <span style={{ color: "#21C55E" }}><CheckGreen /> Added</span>
                    <span style={{ color: "#EF4444" }}><CheckRed /> Removed</span>
                    <span style={{ color: "#3B82F6" }}><CheckBlue /> Modified</span>
                </Col>
                <Col md={6} className="DiffoutputToggleWraper d-flex justify-content-end">
                    {validInputs.map((_, idx) => (
                        <div
                            key={idx}
                            className={`DiffoutputToggle ${activeIndex === idx ? "active" : ""}`}
                            style={{ 
                                backgroundColor: activeIndex === idx ? "#146EEB" : "",
                                cursor: "pointer"
                            }}
                            onClick={() => setActiveIndex(idx)}
                        >
                            {typeLabel} {idx + 1}
                        </div>
                    ))}
                </Col>
            </Row>

            {/* COMPARISON PANELS (BASE vs ACTIVE) */}
            <Row className="Formatter mt-3 json-formatter-row">
                <Col xs={12} lg={6} className="json-formatter-column">
                    <FormatterPanel
                        title={`Base ${typeLabel}`}
                        readOnly
                        customContent={
                            <div 
                                className="diff-container" 
                                dangerouslySetInnerHTML={{ __html: renderColorizedContent(baseContent, true, activeItemDiff, "base") }} 
                            />
                        }
                        footerLeft={`Characters: ${String(baseContent).replace(/\s/g, "").length}`}
                        footerRight={`Size: ${new Blob([baseContent]).size} B`}
                    />
                </Col>

                <Col xs={12} lg={6} className="json-formatter-column">
                    <FormatterPanel
                        title={`${typeLabel} ${activeIndex + 1}`}
                        readOnly
                        editorContainerStyle={{ overflow: "auto" }}
                        wrap={false}
                        customContent={
                            <div 
                                className="diff-container" 
                                dangerouslySetInnerHTML={{ __html: renderColorizedContent(activeJsonContent, true, activeItemDiff, "compare") }} 
                            />
                        }
                        footerLeft={`Characters: ${String(activeJsonContent).replace(/\s/g, "").length}`}
                        footerRight={`Size: ${new Blob([activeJsonContent]).size} B`}
                    />
                </Col>
            </Row>

            {/* INACTIVE ITEMS SECTION */}
            <Row className="Formatter mt-3 json-formatter-row">
                {inactiveItems.map((item) => (
                    <Col xs={12} lg={6} key={item.originalIndex} className="mb-3">
                        <FormatterPanel
                            title={`${typeLabel} ${item.originalIndex + 1}`}
                            readOnly
                            editorContainerStyle={{ overflow: "auto" }}
                            wrap={false}
                            customContent={
                                <div 
                                    className="diff-container" 
                                    dangerouslySetInnerHTML={{ __html: renderColorizedContent(item.content, false) }} 
                                />
                            }
                            footerLeft={`Characters: ${String(item.content).replace(/\s/g, "").length}`}
                            footerRight={`Size: ${new Blob([item.content]).size} B`}
                        />
                    </Col>
                ))}
            </Row>
        </Col>
    );
}

export default JSONDiffViewer;
